import React, { useEffect, useState, useRef } from 'react';
import { Row, Card, Col, Container } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { fetchEmployment } from '../../../actions/employmentActions';
import { fetchEducation } from '../../../actions/educationActions';
import { fetchLanguage } from '../../../actions/languageActions';
import { fetchSections } from '../../../actions/additionalSectionsActions';
import GradientButton from '../../../components/GradientButton';
import html2pdf from 'html2pdf.js';
import { FaArrowLeft } from 'react-icons/fa';
import {
  Download,
  MapPin,
  Phone,
  Mail,
  Calendar,
  Briefcase,
  GraduationCap,
  Languages,
  User,
  Award,
  Users,
  Link,
  Info,
  FileBadge,
  Pin,
} from 'lucide-react';

// Mapowanie nazw sekcji na komponenty ikon
const sectionIconMap = {
  'Podsumowanie zawodowe': User,
  'Osiągnięcia': Award,
  'Stowarzyszenia': Users,
  'Strony, portfolia, profile zawodowe': Link,
  'Dodatkowe informacje': Info,
  'Certyfikaty': FileBadge,
  'default': Pin,
};

function CvGeneratorSummary() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.userDetails);
  const { employments } = useSelector((state) => state.employmentList) || {};
  const { educations } = useSelector((state) => state.educationList) || {};
  const { languages } = useSelector((state) => state.languageList) || {};
  const { sections } = useSelector((state) => state.additionalSectionsList) || {};
  const [backlink, setBacklink] = useState('/cv-career/cv-generator/section/bonus-info');

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem('selectedSections') || '[]');
    const routesMap = {
      check_1: '/cv-career/cv-generator/section/professional-summary',
      check_2: '/cv-career/cv-generator/section/achievements',
      check_3: '/cv-career/cv-generator/section/associations',
      check_4: '/cv-career/cv-generator/section/links',
      check_5: '/cv-career/cv-generator/section/additional-info',
      check_6: '/cv-career/cv-generator/section/certificates',
      check_7: '/cv-career/cv-generator/section/custom',
    };
    if (stored.length > 0) {
      const lastSection = stored[stored.length - 1];
      setBacklink(routesMap[lastSection]);
    } else {
      setBacklink('/cv-career/cv-generator/section/bonus-info');
    }
  }, []);

  useEffect(() => {
    dispatch(fetchEmployment());
    dispatch(fetchEducation());
    dispatch(fetchLanguage());
    dispatch(fetchSections());
  }, [dispatch]);

  const capitalize = (str) =>
    !str ? '' : str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}.${year}`;
  };

  const formatFullDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const cvRef = useRef();

  const handleDownloadPDF = () => {
    const element = cvRef.current;
    
    const images = element.querySelectorAll('img');
    const originalStyles = [];
    
    images.forEach((img, index) => {
      originalStyles[index] = img.style.cssText;
      img.style.objectFit = 'cover';
      img.style.objectPosition = 'center top';
    });

    const opt = {
      margin: [0.5, 0.5, 0.5, 0.5],
      filename: `${user.name || 'cv'}_${user.last_name || ''}.pdf`,
      image: { type: 'jpeg', quality: 1 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        letterRendering: true,
        backgroundColor: '#ffffff',
        scrollX: 0,
        scrollY: 0,
        windowWidth: document.documentElement.offsetWidth,
        logging: false,
        allowTaint: false,
        removeContainer: true,
        imageTimeout: 0,
        onclone: (clonedDoc) => {
          const clonedImages = clonedDoc.querySelectorAll('img');
          clonedImages.forEach((img) => {
            img.style.width = '100px';
            img.style.height = '130px';
            img.style.objectFit = 'cover';
            img.style.objectPosition = 'center top';
            img.style.display = 'block';
          });
        }
      },
      jsPDF: {
        unit: 'px',
        format: [794, 1123],
        orientation: 'portrait',
        hotfixes: ['px_scaling'],
        compress: true
      },
      pagebreak: {
        mode: ['css', 'legacy'],
        before: '.force-page-break',
        avoid: ['.cv-section img', '.cv-section ul'],
      },
    };

    html2pdf()
      .set(opt)
      .from(element)
      .save()
      .then(() => {
        images.forEach((img, index) => {
          img.style.cssText = originalStyles[index];
        });
      })
      .catch((error) => {
        console.error('Błąd generowania PDF:', error);
        images.forEach((img, index) => {
          img.style.cssText = originalStyles[index];
        });
      });
  };

  return (
    <div style={{
      minHeight: '100vh',
      paddingTop: '2rem',
      paddingBottom: '4rem',
      width: '100%'
    }}>
      <Container>
        {/* HEADER */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-4"
        >
          <h1 className="text-white fw-bold mb-2">
            Podsumowanie CV
          </h1>
          <p className="text-white-50">Sprawdź i pobierz swoje CV</p>
        </motion.div>

        <Row>
          <Col lg={6} className="offset-lg-3">
            {/* PRZYCISK POBIERZ */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="mb-4"
            >
                <GradientButton
                  className="w-100 py-3 fw-bold d-flex align-items-center justify-content-center"
                  onClick={handleDownloadPDF}
                  gradient="green"
                >
                <Download size={20} className="me-2" />
                Pobierz jako PDF               
                </GradientButton>
            </motion.div>

            {/* CV PREVIEW */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card
                className="shadow-lg border-0 mb-4"
                style={{ borderRadius: '20px', overflow: 'hidden' }}
              >
                <div ref={cvRef}>
                  <div
                    className="bg-white text-dark p-4"
                    style={{ minHeight: '850px' }}
                  >
                    {/* Informacje o użytkowniku */}
                    <Row className="mb-3">
                      {/* Zdjęcie na górze w wersji mobilnej */}
                      <Col xs={12} className="d-flex d-md-none justify-content-center mb-3">
                        {user.photo && (
                          <div
                            style={{
                              width: '100px',
                              height: '130px',
                              minWidth: '100px',
                              minHeight: '130px',
                              maxWidth: '100px',
                              maxHeight: '130px',
                              background: '#e9ecef',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden',
                              boxShadow: '0 0 6px rgba(0,0,0,0.1)',
                              flexShrink: 0,
                            }}
                          >
                            <img
                              src={
                                user.photo.startsWith('http')
                                  ? user.photo
                                  : `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'}${user.photo}`
                              }
                              alt="Zdjęcie użytkownika"
                              style={{
                                width: '100px',
                                height: '130px',
                                minWidth: '100px',
                                minHeight: '130px',
                                objectFit: 'cover',
                                objectPosition: 'center top',
                                display: 'block',
                              }}
                              crossOrigin="anonymous"
                            />
                          </div>
                        )}
                      </Col>

                      {/* Dane użytkownika po lewej */}
                      <Col md={9}>
                        <h3 className="fw-bold mb-4">
                          {user.name} {user.last_name}
                        </h3>
                        <div>
                          <p className="mb-1">
                            <MapPin size={16} className="me-2 text-primary"/>
                            <strong>Miejsce zamieszkania:</strong> {user.address}, {user.postal_code}{' '}
                            {user.city}
                          </p>
                          <p className="mb-1">
                            <Phone size={16} className="me-2 text-primary"/>
                            <strong>Telefon:</strong> {user.phone}
                          </p>
                          <p className="mb-1">
                            <Mail size={16} className="me-2 text-primary"/>
                            <strong>E-mail:</strong> {user.email}
                          </p>
                          <p className="mb-0">
                            <Calendar size={16} className="me-2 text-primary"/>
                            <strong>Data urodzenia:</strong> {formatFullDate(user.birth_date)}
                          </p>
                        </div>
                      </Col>

                      {/* Zdjęcie po prawej stronie na desktopie */}
                      <Col md={3} className="d-none d-md-flex justify-content-center align-items-start">
                        {user.photo && (
                          <div
                            style={{
                              width: '100px',
                              height: '130px',
                              minWidth: '100px',
                              minHeight: '130px',
                              maxWidth: '100px',
                              maxHeight: '130px',
                              background: '#e9ecef',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden',
                              boxShadow: '0 0 6px rgba(0,0,0,0.1)',
                              flexShrink: 0,
                            }}
                          >
                            <img
                              src={
                                user.photo.startsWith('http')
                                  ? user.photo
                                  : `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'}${user.photo}`
                              }
                              alt="Zdjęcie użytkownika"
                              style={{
                                width: '100px',
                                height: '130px',
                                minWidth: '100px',
                                minHeight: '130px',
                                objectFit: 'cover',
                                objectPosition: 'center top',
                                display: 'block',
                              }}
                              crossOrigin="anonymous"
                            />
                          </div>
                        )}
                      </Col>
                    </Row>

                    {/* Doświadczenie zawodowe */}
                    {employments && employments.length > 0 && (
                      <Row className="mb-3">
                        <Col>
                          <h4 className="fw-bold border-bottom pb-2"><Briefcase size={22} className="me-2 text-primary"/>Doświadczenie zawodowe</h4>
                          {employments.map((emp, index) => (
                            <div key={emp.id || index} className="d-flex mb-3">
                              <div className="col-lg-3">
                                <small>
                                  <strong>
                                    {formatDate(emp.start_date)} –{' '}
                                    {emp.end_date ? formatDate(emp.end_date) : 'Obecnie'}
                                  </strong>
                                </small>
                              </div>
                              <div className="col-lg-9">
                                <p className="mb-1 fw-bold">
                                  <small>{capitalize(emp.position)}</small>
                                </p>
                                <p className="mb-1">
                                  <small>
                                    {capitalize(emp.employer_name)}, {capitalize(emp.city)}
                                  </small>
                                </p>
                                {Array.isArray(emp.description) && emp.description.length > 0 && (
                                  <ul className="mb-0">
                                    {emp.description.map((task, idx) => (
                                      <li key={idx}>
                                        <small>{task}</small>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            </div>
                          ))}
                        </Col>
                      </Row>
                    )}

                    {/* Wykształcenie */}
                    {educations && educations.length > 0 && (
                      <Row className="mb-3">
                        <Col>
                          <h4 className="fw-bold border-bottom pb-2"><GraduationCap size={22} className="me-2 text-primary"/>Wykształcenie</h4>
                          {educations.map((edu, index) => (
                            <div key={edu.id || index} className="d-flex mb-3">
                              <div className="col-lg-3">
                                <small>
                                  <strong>
                                    {formatDate(edu.start_date)} –{' '}
                                    {edu.end_date ? formatDate(edu.end_date) : 'Obecnie'}
                                  </strong>
                                </small>
                              </div>
                              <div className="col-lg-9">
                                <p className="mb-1">
                                  <small>
                                    {capitalize(edu.school_name)} - {capitalize(edu.city)}
                                  </small>
                                </p>
                                <ul className="mb-0">
                                  {Array.isArray(edu.description) && edu.description.length > 0 && (
                                    <>
                                      {edu.description.map((desc, idx) => (
                                        <li key={idx}>
                                          <small>{desc}</small>
                                        </li>
                                      ))}
                                    </>
                                  )}
                                </ul>
                              </div>
                            </div>
                          ))}
                        </Col>
                      </Row>
                    )}

                    {/* Języki */}
                    {languages && languages.length > 0 && (
                      <Row className="mb-3">
                        <Col>
                          <h4 className="fw-bold border-bottom pb-2"><Languages size={22} className="me-2 text-primary"/>Języki</h4>
                          {languages
                            .filter(
                              (lang) =>
                                lang.type === 'język ojczysty' &&
                                lang.name &&
                                lang.name.trim() !== '-'
                            )
                            .map((nativeLang) => (
                              <div key={nativeLang.id} className="mb-3">
                                <p className="fw-bold mb-1">
                                  <small>{capitalize(nativeLang.name)} (język ojczysty)</small>
                                </p>
                              </div>
                            ))}
                          {languages
                            .filter((lang) => lang.type === 'język obcy')
                            .map((lang, index) => {
                              const levels = [
                                { label: 'A1', desc: 'Początkujący' },
                                { label: 'A2', desc: 'Podstawowy' },
                                { label: 'B1', desc: 'Średnio zaawansowany' },
                                { label: 'B2', desc: 'Wyższy średnio zaawansowany' },
                                { label: 'C1', desc: 'Zaawansowany' },
                                { label: 'C2', desc: 'Biegły' },
                              ];
                              const currentLevel = levels.find((l) => l.label === lang.level);
                              const filledCount = levels.findIndex((l) => l.label === lang.level) + 1;

                              return (
                                <div
                                  key={lang.id || index}
                                  className="d-flex justify-content-between align-items-start mb-3"
                                >
                                  <div>
                                    <p className="fw-bold mb-0">
                                      <small>{capitalize(lang.name)}</small>
                                    </p>
                                  </div>
                                  <div className="text-end">
                                    <div className="d-flex justify-content-end gap-1 mb-1">
                                      {levels.map((_, i) => (
                                        <div
                                          key={i}
                                          style={{
                                            width: '16px',
                                            height: '16px',
                                            backgroundColor:
                                              i < filledCount ? '#495057' : '#dee2e6',
                                            borderRadius: '2px',
                                          }}
                                        />
                                      ))}
                                    </div>
                                    <small style={{ color: '#000' }}>
                                      {currentLevel
                                        ? `${currentLevel.desc} (${currentLevel.label})`
                                        : ''}
                                    </small>
                                  </div>
                                </div>
                              );
                            })}
                        </Col>
                      </Row>
                    )}

                    {/* Sekcje dodatkowe */}
                    {sections?.results && sections.results.length > 0 && (
                      <Row>
                        <Col>
                          {sections.results
                            .filter(
                              (section) =>
                                Array.isArray(section.content) && section.content.length > 0
                            )
                            .map((section) => {
                              const SectionIcon = sectionIconMap[section.name] || sectionIconMap['default'];
                              return (
                                <div key={section.id} className="mb-4 cv-section">
                                  <h4 className="fw-bold border-bottom pb-2 text-dark">
                                    <SectionIcon size={22} className="me-2 text-primary"/>{capitalize(section.name)}
                                  </h4>
                                  <ul
                                    style={{ listStyle: 'none', paddingLeft: 0, color: '#000' }}
                                  >
                                    {section.content.map((item, idx) => (
                                      <li key={idx} className="mb-1">
                                        <small>{item}</small>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              );
                            })}
                        </Col>
                      </Row>
                    )}

                    <div
                      className="cv-footer text-center mt-5 pt-4"
                      style={{
                        fontSize: '11px',
                        color: '#555',
                        borderTop: '1px solid #ddd',
                        paddingTop: '8px',
                        pageBreakInside: 'avoid',
                        marginTop: 'auto',
                      }}
                    >
                      <p style={{ marginBottom: 0 }}>
                        Wyrażam zgodę na przetwarzanie moich danych osobowych zawartych w niniejszym CV
                        dla potrzeb niezbędnych do realizacji procesu rekrutacji zgodnie z Rozporządzeniem
                        Parlamentu Europejskiego i Rady (UE) 2016/679 z dnia 27 kwietnia 2016 r. (RODO).
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
            {/* PRZYCISKI NAWIGACJI */}
            <Row className="g-3 mt-2">
              <div className="d-flex justify-content-between">
                <GradientButton
                  onClick={() => navigate(backlink)}
                  gradient="pink"
                >
                  <FaArrowLeft className="me-2" />
                  Wstecz                            
                </GradientButton>

                <GradientButton
                  onClick={() => navigate('/cv-career')}
                  gradient="blue"
                >
                  Zakończ
                </GradientButton>
              </div>
            </Row>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default CvGeneratorSummary